import { z } from "zod";

/**
 * Onboarding checklist step registry — single source of truth for the home
 * "Finish setup" widget. The api derives its zod input enums and step
 * derivation from here; the web derives its presentation map from the same
 * keys, so step keys stay typed end-to-end.
 *
 * A step's `completion` is either:
 *  - `derived`  — computed server-side from existing data (e.g. the org has a
 *                 project). Never stored.
 *  - `tracked`  — an explicit user action we can't otherwise observe (a page
 *                 visit). Stored as a `completed` row in `onboarding_step_states`.
 *
 * A step's `scope` decides how stored state is keyed:
 *  - `user` — global to the user (e.g. personal settings, the same page in
 *             every org). Stored with `organizationId = NULL` and shown as done
 *             in every org's checklist.
 *  - `org`  — specific to one organization. Stored with that `organizationId`.
 *
 * Visibility is gated by an FGA relation (`requiredPermission`), by a
 * deployment-level auth method being enabled (`requiredAuthMethod`), and/or by
 * a deployment setting being configured (`requiredConfig`).
 *
 * Every step is additionally manually dismissable, and the whole widget can be
 * dismissed via the {@link WIDGET_DISMISS_KEY} sentinel (also stored in
 * `onboarding_step_states`, but not a real step; org-scoped).
 */
export interface OnboardingStep {
	key: OnboardingStepKey;
	/** FGA relation on the `organization` object gating visibility, if any. */
	requiredPermission?:
		| "can_create_project"
		| "can_manage_members"
		| "can_manage_settings";
	/** Auth method the deployment must have enabled to show the step, if any. */
	requiredAuthMethod?: "email-otp";
	/** Deployment setting that must be configured to show the step, if any. */
	requiredConfig?: "supportBookingUrl";
	completion: "derived" | "tracked";
	scope: "user" | "org";
}

export const ONBOARDING_STEPS = [
	{
		key: "personal",
		requiredPermission: undefined,
		requiredAuthMethod: undefined,
		completion: "tracked",
		scope: "user",
	},
	{
		// Passkeys ride on email OTP (see web `useAuthConfig`), so the step is
		// only offered where email OTP is enabled. Complete = user has ≥ 1
		// passkey.
		key: "passkey",
		requiredPermission: undefined,
		requiredAuthMethod: "email-otp",
		completion: "derived",
		scope: "user",
	},
	{
		key: "create_project",
		requiredPermission: "can_create_project",
		requiredAuthMethod: undefined,
		completion: "derived",
		scope: "org",
	},
	{
		key: "invite_team",
		requiredPermission: "can_manage_members",
		requiredAuthMethod: undefined,
		completion: "derived",
		scope: "org",
	},
	{
		key: "organization_settings",
		requiredPermission: "can_manage_settings",
		requiredAuthMethod: undefined,
		completion: "tracked",
		scope: "org",
	},
	{
		// Opens the help center in a new tab; the click marks it done.
		key: "help_center",
		requiredPermission: undefined,
		requiredAuthMethod: undefined,
		completion: "tracked",
		scope: "user",
	},
	{
		// Opens the support booking page; offered only where one is configured.
		key: "book_meeting",
		requiredPermission: undefined,
		requiredAuthMethod: undefined,
		requiredConfig: "supportBookingUrl",
		completion: "tracked",
		scope: "user",
	},
] as const satisfies readonly OnboardingStep[];

/** Step keys whose stored state is global to the user (orgId = NULL). */
export const USER_SCOPED_STEP_KEYS = ONBOARDING_STEPS.filter(
	(s) => s.scope === "user",
).map((s) => s.key) as Array<
	Extract<(typeof ONBOARDING_STEPS)[number], { scope: "user" }>["key"]
>;

/** True when this key's stored state is keyed by user alone (orgId = NULL). */
export const isUserScopedStepKey = (key: string): boolean =>
	(USER_SCOPED_STEP_KEYS as readonly string[]).includes(key);

export type OnboardingStepKey =
	| "personal"
	| "passkey"
	| "create_project"
	| "invite_team"
	| "organization_settings"
	| "help_center"
	| "book_meeting";

export const ONBOARDING_STEP_KEYS = ONBOARDING_STEPS.map(
	(s) => s.key,
) as OnboardingStepKey[];

/**
 * Tracked steps complete via an explicit user action (auto-on-visit), so they
 * are the only keys `completeStep` accepts.
 */
export const TRACKED_STEP_KEYS = ONBOARDING_STEPS.filter(
	(s) => s.completion === "tracked",
).map((s) => s.key) as Array<
	Extract<(typeof ONBOARDING_STEPS)[number], { completion: "tracked" }>["key"]
>;

/**
 * Reserved sentinel `stepKey` for the whole-widget dismiss. Stored in the same
 * table but is NOT a real step (no permission gate, no presentation entry).
 */
export const WIDGET_DISMISS_KEY = "_widget" as const;
export type WidgetDismissKey = typeof WIDGET_DISMISS_KEY;

/** Keys `completeStep` accepts: tracked steps only. */
export const completableStepKey = z.enum(
	TRACKED_STEP_KEYS as [string, ...string[]],
);

/** Keys `dismissStep` accepts: any real step plus the widget sentinel. */
export const dismissableStepKey = z.enum([
	...ONBOARDING_STEP_KEYS,
	WIDGET_DISMISS_KEY,
] as unknown as [string, ...string[]]);

export type OnboardingStepStatus = "incomplete" | "completed" | "dismissed";
